import React, { useState, useEffect, useMemo, useContext } from 'react';
import Button from '../../../../../components/UI/Button';
import Card from '../../UI/Card';
import CheckListItem from '../../../../../components/UI/CheckListItem';
import css from './approved.module.scss';
import LegalText from '../../UI/LegalText';
import { useCreditClient } from '../../../../../services/credit';
import Divider from '../../UI/Divider';
import InteractiveText from '../../UI/InteractiveText';
import Modal from '../../UI/Modal';
import { AnalyticsContext } from '../../../../../ModularReduxAnalytics';

export default function Approved({
  authToken,
  moveToOfferAccepted,
  moveToContinueWithoutCredit
}) {
  const creditApplicationClient = useMemo(() => useCreditClient(), []);
  const [postTermsDocuments, setPostTermsDocuments] = useState({});
  const [displayDeclinedModal, setDisplayDeclinedModal] = useState(false);
  const { analytics } = useContext(AnalyticsContext);

  const getLegalPostTermsDocuments = async () => {
    await creditApplicationClient
      .getApprovalDocuments({
        authToken: authToken,
        documentsType: 'post-terms'
      })
      .then(documentsList => {
        const newPostTermsDocuments = {};
        documentsList?.map(document => {
          newPostTermsDocuments[document.type] = document.content;
        });
        setPostTermsDocuments(newPostTermsDocuments);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.page('Registration', 'Credit Application Approved');
    getLegalPostTermsDocuments();
  }, []);

  const apr = creditApplicationClient.getAPR();
  const creditLimit = creditApplicationClient.getCreditLimit();

  const declineOffer = () => {
    moveToContinueWithoutCredit();
  };

  return (
    <div className={css.approved}>
      {displayDeclinedModal && (
        <Modal
          header="Are you sure?"
          subheader="You can come back and accept this offer within 20 days. Download the app to manage your credit application."
          option="Close"
          optionAction={() => setDisplayDeclinedModal(false)}
          secondaryOption="I'm Sure"
          secondaryOptionAction={declineOffer}
        />
      )}
      <h1>You're approved!</h1>
      <h2>
        Please review the cardholder terms and conditions. Your card will arrive
        in 7-10 business days after you’ve accepted this offer and finished
        setting up your account.
      </h2>
      <div className={css.cardImage} />
      <Card>
        <div className={css.creditLimit}>
          <p className={css.creditLimitNumber}>{`$${creditLimit}`}</p>
          <p className={css.creditLimitText}>Credit Limit</p>
        </div>
        <Divider />
        <div className={css.creditInfoContainer}>
          <div>
            <div className={css.creditInfo}>
              <p className={css.creditInfoHeader}>Annual fee</p>
              <p className={css.creditInfoText}>$0</p>
            </div>
            <div className={css.creditInfo}>
              <p className={css.creditInfoHeader}>Purchase APR</p>
              <p className={css.creditInfoText}>{`${apr}%`}</p>
            </div>
          </div>
          <div>
            <div className={css.creditInfo}>
              <p className={css.creditInfoHeader}>Cash back</p>
              <p className={css.creditInfoText}>Up to 3%</p>
            </div>
            <div className={css.creditInfo}>
              <p className={css.creditInfoHeader}>Invest</p>
              <p className={css.creditInfoText}>Automatic</p>
            </div>
          </div>
        </div>
        <div className={css.checklistContainer}>
          <CheckListItem>
            <p>Cash back when you spend</p>
          </CheckListItem>
          <CheckListItem>
            <p>Flexible reward redemption options</p>
          </CheckListItem>
          <CheckListItem>
            <p>Your family’s finances in one app</p>
          </CheckListItem>
        </div>
      </Card>
      <Button
        text="Accept offer"
        onClick={() => {
          analytics.trackEvent('Credit Action', {
            eventProps: {
              type: 'Accept offer',
              page: 'Credit Application Approved'
            }
          });
          moveToOfferAccepted();
        }}
      />
      <InteractiveText
        text={'Not now'}
        handleClick={() => {
          analytics.trackEvent('Credit Action', {
            eventProps: {
              type: 'Not now',
              page: 'Credit Application Approved'
            }
          });
          setDisplayDeclinedModal(true);
        }}
      />
      <LegalText>
        <p>
          By tapping “Accept offer” I accept the terms of this offer and have
          read and agree to the terms set forth in the{' '}
          <a
            data-e2e="cardholder-agreement"
            target="_blank"
            href={postTermsDocuments['card_member_agreement']}
          >
            Cardmember Agreement
          </a>
          ,{' '}
          <a
            data-e2e="cardholder-agreement"
            target="_blank"
            href={postTermsDocuments['terms_schedule']}
          >
            Terms Schedule
          </a>
          ,{' '}
          <a
            data-e2e="cardholder-agreement"
            target="_blank"
            href={postTermsDocuments['rewards_disclosure']}
          >
            Rewards Terms & Conditions
          </a>{' '}
          and{' '}
          <a
            data-e2e="cardholder-agreement"
            target="_blank"
            href={postTermsDocuments['benefits_disclosure']}
          >
            Guide to Benefits
          </a>
          .
        </p>
      </LegalText>
    </div>
  );
}
